// src/components/SalonCard/SalonCard.js
// Version FINALE avec tooltip sur badge +X

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import favoritesService from '../../services/favoritesService';
import { getSalonImage, SALON_FALLBACK_IMAGE } from '../../constants/salonImages';
import './SalonCard.css';

const SalonCard = ({ salon, onFavoriteRemoved }) => {
  const navigate = useNavigate();
  const [isFavorite, setIsFavorite] = useState(salon.is_favorite || false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [imgSrc, setImgSrc] = useState(() => getSalonImage(salon));

  useEffect(() => {
    setImgSrc(getSalonImage(salon));
  }, [salon]);

  // Calculer si le salon est nouveau (créé il y a moins de 15 jours)
  const isNew = () => {
    if (!salon.created_at) return false;
    const createdDate = new Date(salon.created_at);
    const now = new Date();
    const diffDays = Math.floor((now - createdDate) / (1000 * 60 * 60 * 24));
    return diffDays <= 15;
  };

  // ✅ Toggle favoris avec VRAI appel API
  const handleToggleFavorite = async (e) => {
    e.stopPropagation(); // Empêcher la navigation
    
    try {
      setFavoriteLoading(true);
      
      // Appel API toggle
      const response = await favoritesService.toggleFavorite(salon.id);
      
      // Mettre à jour l'état local
      setIsFavorite(response.data.isFavorite);
      
      // Si on est sur la page Favoris et qu'on retire le favori
      if (!response.data.isFavorite && onFavoriteRemoved) {
        // Callback pour retirer de la liste (sur page Favoris)
        onFavoriteRemoved(salon.id);
      }
      
      // Afficher un message de succès (optionnel)
      console.log('✅', response.message);
      
    } catch (error) {
      console.error('❌ Erreur toggle favoris:', error);
      
      // Afficher un message d'erreur (optionnel)
      if (error.response?.status === 401) {
        alert('Vous devez être connecté pour ajouter des favoris');
      } else {
        alert('Une erreur est survenue. Veuillez réessayer.');
      }
    } finally {
      setFavoriteLoading(false);
    }
  };

  // Clic sur la carte → Navigation vers détails
  const handleCardClick = () => {
    navigate(`/salon/${salon.id}`);
  };

  // Formater les spécialités
  const getSpecialties = () => {
    if (!salon.specialties) return [];
    
    try {
      // Si c'est déjà un array
      if (Array.isArray(salon.specialties)) {
        return salon.specialties;
      }
      
      // Si c'est une string JSON
      if (typeof salon.specialties === 'string') {
        return JSON.parse(salon.specialties);
      }
      
      // Si c'est un objet
      if (typeof salon.specialties === 'object') {
        return [salon.specialties];
      }
      
      return [];
    } catch (error) {
      console.error('Erreur parsing specialties:', error);
      return [];
    }
  };

  // Icônes pour les spécialités
  const getSpecialtyIcon = (specialty) => {
    // Si specialty est un objet, extraire le nom
    const specialtyName = typeof specialty === 'string' 
      ? specialty 
      : specialty?.name || specialty?.label || String(specialty);
    
    const icons = {
      'Tatouage': '✂️',
      'Coiffure': '✂️',
      'Tresses': '💇',
      'Soins': '💆',
      'Spa': '🧖',
      'Massage': '💆',
      'Maquillage': '💄',
      'Esthétique': '💅',
      'Manucure': '💅',
      'Pédicure': '🦶',
      'Onglerie': '💅',
      'Barbier': '💈',
      'Sauna': '🧖',
    };
    
    // Chercher une correspondance partielle
    for (const [key, icon] of Object.entries(icons)) {
      if (specialtyName.toLowerCase().includes(key.toLowerCase())) {
        return icon;
      }
    }
    
    return '🏷️'; // Icône par défaut
  };

  const specialties = getSpecialties();
  const showNew = isNew();

  const hiddenSpecialties = specialties.slice(3);

  const ratingValue = parseFloat(salon.average_rating || salon.rating || 0);
  const reviewCount = salon.review_count || salon.total_reviews || 0;

  return (
    <div className="salon-card" onClick={handleCardClick}>
      {/* Image avec badges */}
      <div className="salon-card-image-container">
        <div className="salon-card-image">
          <img
            src={imgSrc}
            alt={salon.name}
            className={imgSrc === SALON_FALLBACK_IMAGE ? 'salon-card-img--logo' : undefined}
            onError={() => setImgSrc(SALON_FALLBACK_IMAGE)}
          />
        </div>

        {/* Badges en haut */}
        <div className="salon-card-badges-top">
          {/* Badge type de service (à gauche) */}
          {salon.service_type === 'physical' && (
            <span className="badge badge-salon">Au salon</span>
          )}
          {salon.service_type === 'home' && (
            <span className="badge badge-home">Déplacement</span>
          )}
          {salon.service_type === 'both' && (
            <span className="badge badge-both">Les deux</span>
          )}

          {/* Badge NOUVEAU (à droite des badges, mais avant le cœur) */}
          {showNew && (
            <span className="badge badge-new">NOUVEAU</span>
          )}
        </div>

        {/* ⭐ Badge note (en bas à gauche de l'image) */}
        {ratingValue > 0 && (
          <div className="salon-card-rating-badge">
            <span className="rating-star">⭐</span>
            <span className="rating-value">{ratingValue.toFixed(1)}</span>
            {reviewCount > 0 && (
              <span className="rating-count">({reviewCount})</span>
            )}
          </div>
        )}

        {/* ❤️ Bouton Favoris (tout à droite) */}
        <button 
          className={`salon-card-favorite ${isFavorite ? 'is-favorite' : ''}`}
          onClick={handleToggleFavorite}
          disabled={favoriteLoading}
          aria-label={isFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
        >
          {favoriteLoading ? (
            <span className="favorite-spinner">⏳</span>
          ) : (
            <svg 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill={isFavorite ? 'currentColor' : 'none'}
              stroke="currentColor" 
              strokeWidth="2"
            >
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
          )}
        </button>
      </div>

      {/* Infos du salon */}
      <div className="salon-card-content">
        {/* Nom du salon */}
        <h3 className="salon-card-title">{salon.name}</h3>

        {/* 📍 Adresse AVEC VILLE */}
        <p className="salon-card-address">
          📍 {salon.address}{salon.city ? `, ${salon.city}` : ''}
        </p>

        {/* 🏷️ Tags spécialités avec icônes */}
        {specialties.length > 0 && (
          <div className="salon-card-tags">
            {specialties.slice(0, 3).map((specialty, index) => {
              // Extraire le nom si c'est un objet
              const specialtyName = typeof specialty === 'string' 
                ? specialty 
                : specialty?.name || specialty?.label || String(specialty);
              
              return (
                <span key={index} className="specialty-tag">
                  <span className="tag-icon">{getSpecialtyIcon(specialty)}</span>
                  <span className="tag-text">{specialtyName}</span>
                </span>
              );
            })}
            
            {/* ✅ Badge +X avec TOOLTIP */}
            {specialties.length > 3 && (
              <span className="specialty-tag tag-more tag-with-tooltip">
                +{specialties.length - 3}
                
                {/* Tooltip qui s'affiche au survol */}
                <span className="tag-tooltip">
                  {hiddenSpecialties.map((specialty, index) => {
                    const specialtyName = typeof specialty === 'string' 
                      ? specialty 
                      : specialty?.name || specialty?.label || String(specialty);
                    
                    return (
                      <span key={index} className="tooltip-item">
                        <span className="tooltip-icon">{getSpecialtyIcon(specialty)}</span>
                        <span className="tooltip-text">{specialtyName}</span>
                      </span>
                    );
                  })}
                </span>
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SalonCard;